import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Combobox, ComboboxItem, IconGear, Wrapper } from '@screentone/core';

import { useContentIdTracking } from 'contexts/content-id-tracking/useContentIdTracking';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import {
  PublicationSettingSearchableContentType,
  QueryItem,
  UiFeedsTreatmentType,
  useDedupedArtifactQuery
} from 'data/generated/graphql';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { QueryModal } from 'features/section-page-edit/components/page-draft-section/components/query-modal/QueryModal';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import {
  AllessehContentQueryBody,
  AllessehQueryRuleTerm,
  AllessehQueryRuleTerms,
  DEFAULT_TOPICS_QUERY
} from 'hooks/useAllessehContentQuery';
import { TopicsResponse, useTopicsQuery } from 'hooks/useTopicsQuery';
import { BaseQueryOrigin, getBaseQuery } from 'utils/contentType';
import { safelyParseContent } from 'utils/temp';
import styles from './Feed.module.scss';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { QueryProps } from '../Query';

enum BarronsTerms {
  SubjectCode = 'SubjectCode',
  BarronsDisplayBrand = 'BarronsDisplayBrand'
}

const DEFAULT_TOPIC = {
  url: '',
  seoDisplayName: '',
  djmlKeyword: '',
  subject: false,
  brand: false
};

const FEED_COUNT_BY_TREATMENT: Partial<Record<UiFeedsTreatmentType, number>> = {
  [UiFeedsTreatmentType.Feeds_3Stories]: 3,
  [UiFeedsTreatmentType.Feeds]: 4
};

const DEFAULT_FEED_COUNT = 5;

interface FeedProps extends QueryProps {
  hierarchyId: string;
  isHistory: boolean;
  treatmentTypeSetting?: Record<string, unknown>;
  isReadOnly?: boolean;
  // modifyEntity: (hierarchyId: string, callback: (entity: QueryItem) => void) => void;
}

// TODO: this component is very barrons specific, when more feeds are added, this should be refactored
export const Feed = ({
  data: query,
  hierarchyId,
  isHistory,
  ignoreBaseQuery,
  treatmentTypeSetting,
  isReadOnly = false
}: FeedProps) => {
  const parsedQuery: AllessehContentQueryBody = query.attributes.query;
  const moduleTreatment = treatmentTypeSetting?.treatmentType as UiFeedsTreatmentType | undefined;
  const count = moduleTreatment ? (FEED_COUNT_BY_TREATMENT[moduleTreatment] ?? DEFAULT_FEED_COUNT) : DEFAULT_FEED_COUNT;
  const [filteredOptions, setFilteredOptions] = useState<TopicsResponse[]>([]);
  const [option, setOption] = useState<TopicsResponse>(DEFAULT_TOPIC);
  const { data: topicsData } = useTopicsQuery();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currentProperty = useConvertedProperty();
  const { modifyEntity, getDTO, root, hasModelChangedOnce, setIsQueryLoading, applyDedupedRoot } =
    useDataModelContext();
  const { data: publicationSettingsResp } = usePublicationSettings();
  const baseQuery = getBaseQuery({
    contentTypes: [PublicationSettingSearchableContentType.Article],
    publicationSettings: publicationSettingsResp?.publicationSetting,
    origin: BaseQueryOrigin.QUERY_BUILDER
  });
  const { trackContentIds, ignoreContentIds } = useContentIdTracking();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const artifactDTO = useMemo(() => getDTO(true, true), [root.collection]);

  const { data: dedupedData, isFetching: dedupedIsLoading } = useDedupedArtifactQuery(
    { dedupedArtifactInput: { artifactDTO, publicationKey: currentProperty! } },
    {
      enabled:
        !!currentProperty &&
        hasModelChangedOnce &&
        !isHistory &&
        // @ts-expect-error Query is not typed as a `AllessehContentQueryBody`
        query.attributes.query.count > 0,
      staleTime: 5000
    }
  );

  const isLoading = dedupedIsLoading;

  useLayoutEffect(() => {
    setIsQueryLoading(isLoading);
  }, [isLoading, setIsQueryLoading]);

  useEffect(() => {
    if (dedupedData && 'root' in dedupedData.dedupedArtifact) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      applyDedupedRoot(dedupedData.dedupedArtifact.root);
    }
  }, [dedupedData, applyDedupedRoot]);

  useEffect(() => {
    if (query.queryItems && !isLoading) {
      trackContentIds(
        hierarchyId,
        query.queryItems.map((item) => item.attributes.id)
      );
    }

    return () => {
      if (query.queryItems && !isLoading) {
        ignoreContentIds(
          hierarchyId,
          query.queryItems.map((item) => item.attributes.id)
        );
      }
    };
  }, [hierarchyId, query.queryItems, trackContentIds, ignoreContentIds, isLoading]);

  // const hierarchyId = generateHierarchyId(query, parentHierarchyId, index);
  useEffect(() => {
    if (topicsData) {
      setFilteredOptions(topicsData);
      const option = parsedQuery.query?.and?.find((item) => {
        const termsItem = item as AllessehQueryRuleTerms;
        const termItem = item as AllessehQueryRuleTerm;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (termItem.term) {
          return [BarronsTerms.SubjectCode, BarronsTerms.BarronsDisplayBrand].includes(
            termItem.term.key as BarronsTerms
          );
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (termsItem.terms) {
          return [BarronsTerms.SubjectCode, BarronsTerms.BarronsDisplayBrand].includes(
            termsItem.terms.key as BarronsTerms
          );
        }
        return null;
      });
      const barronsMatch = topicsData.find((topic) => {
        if (option) {
          if ('term' in option) {
            return option.term.value === topic.djmlKeyword;
          }
          if ('terms' in option) {
            return option.terms.value.join(',') === topic.djmlKeyword;
          }
        }
        return null;
      });
      // MW-shape fallback: match the saved feedTitle against the topic's display name.
      const {
        attributes: { feedTitle }
      } = query;
      const mwMatch = feedTitle ? topicsData.find((t) => t.seoDisplayName === feedTitle) : undefined;
      setOption(barronsMatch ?? mwMatch ?? topicsData[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedQuery, parsedQuery.query?.and, topicsData, query.attributes]);

  const handleChange = (
    selectedItemOrFn: ComboboxItem | null | ((prevState: ComboboxItem | null) => ComboboxItem | null)
  ) => {
    const selectedItem = typeof selectedItemOrFn === 'function' ? selectedItemOrFn(null) : selectedItemOrFn;
    if (!selectedItem) return;
    const selectedOption = filteredOptions.find(
      (option) => option.djmlKeyword === selectedItem.value
    ) as TopicsResponse;
    setOption(selectedOption);
    const parsedTopicsQuery: AllessehContentQueryBody = safelyParseContent(DEFAULT_TOPICS_QUERY);
    if (selectedOption.contentFeeds?.length) {
      selectedOption.contentFeeds.forEach((feed) => {
        const term: AllessehQueryRuleTerms = { terms: { key: feed.type, value: [feed.query] } };
        // MW operator semantics: OR/AND both mean "include this filter as a required match" → Allesseh `and`.
        // Only NOT routes to the exclusion bucket.
        const bucket = feed.operator === 'NOT' ? (parsedTopicsQuery.query!.not ??= []) : parsedTopicsQuery.query?.and;
        bucket?.push(term);
      });
    } else {
      const termKey = selectedOption.brand ? BarronsTerms.BarronsDisplayBrand : BarronsTerms.SubjectCode;
      parsedTopicsQuery.query?.and?.push({ terms: { key: termKey, value: String(selectedItem.value).split(',') } });
    }
    const newJsonQueryStr = JSON.stringify(parsedTopicsQuery);
    modifyEntity(hierarchyId, (entity) => {
      const query = entity as QueryItem;
      query.attributes.isFeed = true;
      query.attributes.feedTitle = selectedOption.seoDisplayName;
      query.attributes.feedUrl = selectedOption.url;
      query.attributes.query = JSON.parse(newJsonQueryStr) as Record<string, unknown>;
      // count is set based on treatment type (e.g., 3 for 3-stories feeds, 5 default)
      query.attributes.query.count = count;
    });
  };

  // Auto-pick the first topic on first load when the feed has no saved selection,
  // so the gear modal doesn't open an empty query. Gated on filteredOptions so
  // handleChange's `filteredOptions.find(...)` has data to look up.
  const didAutoSelectRef = useRef(false);
  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (!filteredOptions.length) return;
    if (query.attributes.feedTitle) return;
    const hasExistingRule =
      (parsedQuery.query?.and?.length ?? 0) > 0 ||
      (parsedQuery.query?.or?.length ?? 0) > 0 ||
      (parsedQuery.query?.not?.length ?? 0) > 0;
    if (hasExistingRule) return;
    didAutoSelectRef.current = true;
    const first = filteredOptions[0];
    handleChange({ id: first.djmlKeyword, value: first.djmlKeyword, label: first.seoDisplayName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOptions, parsedQuery, query.attributes.feedTitle]);

  const allessehJsonQuery = useMemo(
    () =>
      typeof query.attributes.query === 'string' ? query.attributes.query : JSON.stringify(query.attributes.query),
    [query.attributes.query]
  );

  const handleChangeJsonQueryStr = useCallback(
    (newJsonQueryStr: string) => {
      modifyEntity(hierarchyId, (entity) => {
        const query = entity as QueryItem;
        query.attributes.query = JSON.parse(newJsonQueryStr) as Record<string, unknown>;
      });
    },
    [hierarchyId, modifyEntity]
  );

  const items = useMemo(
    () =>
      filteredOptions.map((option) => ({
        id: option.djmlKeyword,
        value: option.djmlKeyword,
        label: option.seoDisplayName
      })),
    [filteredOptions]
  );

  // memoize selectedItem to prevent Combobox from resetting its internal state
  const selectedItem = useMemo(
    () => ({
      id: option.djmlKeyword,
      value: option.djmlKeyword,
      label: option.seoDisplayName
    }),
    [option.djmlKeyword, option.seoDisplayName]
  );

  return (
    <>
      <Box data-model-hierarchy-id={hierarchyId} className={styles.cardContainer} data-testid="feed-card">
        <Wrapper padding={{ all: 'sm' }} className={styles.cardHeader} data-testid="feed-card-header">
          {items.length > 0 && (
            <Combobox
              placeholder="Select a Feed"
              selectedItem={selectedItem}
              onSelectedItemChange={handleChange}
              disabled={isHistory || items.length === 0 || isReadOnly}
              items={items}
              showReset={false}
              status={items.length > 0 ? 'ready' : 'loading'}
              data-testid="feed-combobox"
            />
          )}
          {items.length === 0 && option.seoDisplayName}
          <Button
            onClick={() => {
              setIsModalOpen(true);
            }}
            tertiary
            margin={{ left: 'sm' }}
            icon={IconGear}
            data-testid="feed-card-settings-button"
          />
        </Wrapper>
      </Box>
      {isModalOpen && (
        <QueryModal
          queryId={query.attributes.id}
          queryItemHierarchyId={hierarchyId}
          allessehJsonQuery={allessehJsonQuery}
          onDismiss={() => setIsModalOpen(false)}
          onChangeJsonQueryStr={handleChangeJsonQueryStr}
          count={count}
          baseQuery={baseQuery}
          ignoreBaseQuery={ignoreBaseQuery}
          queryRuleDisabledFlags={{ all: true }}
          queryRuleHiddenFlags={{ any: true }}
          queryRuleFilterKey={{ not: ['UpstreamOriginId'] }}
          viewOnly={isHistory || isReadOnly}
        />
      )}
    </>
  );
};
